import { toHiragana, toRomaji } from 'wanakana';
import type { Song } from './types';

function stripPunctuation(value: string) {
  return value
    .replace(/[^\p{L}\p{N}\s]/gu, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function getLevenshteinDistance(a: string, b: string) {
  if (!a.length) return b.length;
  if (!b.length) return a.length;

  const matrix = Array.from({ length: b.length + 1 }, (_, index) => [index]);

  for (let index = 0; index <= a.length; index++) {
    matrix[0][index] = index;
  }

  for (let row = 1; row <= b.length; row++) {
    for (let column = 1; column <= a.length; column++) {
      if (b.charAt(row - 1) === a.charAt(column - 1)) {
        matrix[row][column] = matrix[row - 1][column - 1];
      } else {
        matrix[row][column] = Math.min(
          matrix[row - 1][column - 1] + 1,
          matrix[row][column - 1] + 1,
          matrix[row - 1][column] + 1
        );
      }
    }
  }

  return matrix[b.length][a.length];
}

function maxDistanceForLength(length: number) {
  if (length <= 4) return 0;
  if (length <= 8) return 1;
  if (length <= 12) return 2;
  return 3;
}

type SearchField = {
  value: string;
  weight: number;
  fuzzy?: boolean;
};

function getSearchFields(song: Song): SearchField[] {
  return [
    { value: song.title, weight: 120, fuzzy: true },
    { value: song.englishName ?? '', weight: 115, fuzzy: true },
    { value: song.phoneticName ?? '', weight: 110, fuzzy: true },
    { value: song.titleFurigana ?? '', weight: 105, fuzzy: true },
    { value: song.description, weight: 80 },
    { value: song.unitName ?? '', weight: 75 },
    { value: song.centerCharacterName ?? '', weight: 90, fuzzy: true },
    { value: song.centerCharacterEnglishName ?? '', weight: 90, fuzzy: true },
    ...(song.characterNames ?? []).map((value) => ({ value, weight: 85, fuzzy: true })),
    ...(song.characterEnglishNames ?? []).map((value) => ({ value, weight: 85, fuzzy: true }))
  ];
}

function getQueryForms(query: string) {
  const q = query.toLowerCase();
  const strippedQuery = stripPunctuation(q);
  const queryHiragana = toHiragana(strippedQuery, { passRomaji: false });
  const queryRomaji = toRomaji(queryHiragana).replace(/\s+/g, '');

  return {
    q,
    strippedQuery,
    queryHiragana,
    queryRomaji
  };
}

function scoreField(field: SearchField, query: ReturnType<typeof getQueryForms>) {
  const value = field.value.toLowerCase();
  if (!value) return 0;

  const strippedValue = stripPunctuation(value);
  const valueHiragana = toHiragana(strippedValue, { passRomaji: false });
  const valueRomaji = toRomaji(valueHiragana).replace(/\s+/g, '');

  if (
    value === query.q ||
    strippedValue === query.strippedQuery ||
    valueHiragana === query.queryHiragana ||
    valueRomaji === query.queryRomaji
  ) {
    return field.weight + 100;
  }

  if (
    value.startsWith(query.q) ||
    strippedValue.startsWith(query.strippedQuery) ||
    valueHiragana.startsWith(query.queryHiragana) ||
    valueRomaji.startsWith(query.queryRomaji)
  ) {
    return field.weight + 80;
  }

  if (
    value.includes(query.q) ||
    strippedValue.includes(query.strippedQuery) ||
    valueHiragana.includes(query.queryHiragana) ||
    valueRomaji.includes(query.queryRomaji)
  ) {
    return field.weight + 60;
  }

  if (!field.fuzzy) {
    return 0;
  }

  const strippedDistance = getLevenshteinDistance(strippedValue, query.strippedQuery);
  if (
    Math.abs(strippedValue.length - query.strippedQuery.length) <= 2 &&
    strippedDistance <= maxDistanceForLength(query.strippedQuery.length)
  ) {
    return field.weight + 40 - strippedDistance;
  }

  const romajiDistance = getLevenshteinDistance(valueRomaji, query.queryRomaji);
  if (
    query.queryRomaji &&
    Math.abs(valueRomaji.length - query.queryRomaji.length) <= 2 &&
    romajiDistance <= maxDistanceForLength(query.queryRomaji.length)
  ) {
    return field.weight + 30 - romajiDistance;
  }

  return 0;
}

export function fuzzySearch(song: Song, query: string) {
  return getSearchScore(song, query) > 0;
}

export function getSearchScore(song: Song, query: string) {
  if (!query.trim()) return 0;

  const queryForms = getQueryForms(query);
  let bestScore = 0;

  for (const field of getSearchFields(song)) {
    bestScore = Math.max(bestScore, scoreField(field, queryForms));
  }

  return bestScore;
}
